import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import {
  Mic,
  Brain,
  Sparkles,
  Bot,
  MapPin,
  GraduationCap,
  CheckCircle2,
  ArrowRight,
  MessageCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import logoFull from "@/assets/logo-full-navy.svg";

type Lang = "id" | "en";

const WHATSAPP_NUMBER = "6281310833434";
const WA_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Halo, saya bertemu SORIN di Indonesia Miners 2026. Boleh info lebih lanjut?"
)}`;

const copy = {
  id: {
    eventBadge: "Indonesia Miners 2026",
    headline: "AI yang mencegah kecelakaan tambang — sebelum terjadi.",
    subhead:
      "Bukan aplikasi laporan. Ini otak keselamatan untuk seluruh tambang Anda.",
    pitchTitle: "Apa itu SORIN? (versi gampang)",
    pitchBody:
      "Bayangkan setiap pekerja punya mentor keselamatan pribadi yang ngobrol via WhatsApp — tahu siapa yang lelah hari ini, di mana bahaya menumpuk, dan ngasih tahu supervisor harus bertindak — sebelum ada yang celaka.",
    howTitle: "Cara kerjanya, 3 langkah",
    steps: [
      {
        n: "1",
        t: "DENGAR",
        d: "Pekerja ngobrol via WhatsApp — suara atau teks. 10 detik selesai.",
      },
      {
        n: "2",
        t: "PREDIKSI",
        d: "AI baca pola kelelahan, stres, near-miss, dan perilaku berisiko.",
      },
      {
        n: "3",
        t: "BERTINDAK",
        d: "Supervisor dapat tugas spesifik — bukan email panjang.",
      },
    ],
    capabilityTitle: "Yang bisa SORIN lakukan",
    capabilitySub: "Satu platform, kemampuan lengkap untuk HSE modern.",
    cards: [
      {
        icon: Mic,
        t: "Lapor Bahaya 10 Detik",
        d: "Pekerja ngomong, AI ngerti, laporan jadi otomatis.",
      },
      {
        icon: Brain,
        t: "Profil Risiko Pekerja",
        d: "Tahu siapa berisiko tinggi sebelum mereka celaka.",
      },
      {
        icon: Sparkles,
        t: "Prediksi Insiden",
        d: "AI baca sinyal lemah dan kasih peringatan dini.",
      },
      {
        icon: Bot,
        t: "Action Recommender",
        d: "Supervisor dapat saran tindakan otomatis dari AI.",
      },
      {
        icon: MapPin,
        t: "Peta Bahaya Real-time",
        d: "Lihat hotspot bahaya per lokasi dan per shift.",
      },
      {
        icon: GraduationCap,
        t: "Micro-learning Otomatis",
        d: "Pelatihan 60 detik dikirim ke pekerja yang butuh.",
      },
    ],
    diffTitle: "Beda dari yang lain",
    diff: [
      "Native Bahasa Indonesia & WhatsApp — pekerja gak perlu install apa-apa.",
      "Berbasis ilmu perilaku (UPPS-P psikometri), bukan sekadar form digital.",
      "Dirancang untuk tambang Indonesia: site terpencil, sinyal lemah, shift panjang.",
    ],
    fitTitle: "SORIN cocok kalau Anda…",
    fit: [
      "Punya 50+ pekerja lapangan",
      "Pakai banyak kontraktor",
      "Sulit dapat leading indicator yang nyata",
      "Capek pakai sistem HSE yang cuma jadi tempat input form",
    ],
    ctaTitle: "Mau lihat SORIN bekerja di tambang Anda?",
    ctaSub:
      "Tinggalkan kontak — kami kirim demo singkat 15 menit, tanpa jualan keras.",
    name: "Nama",
    company: "Perusahaan",
    role: "Jabatan (opsional)",
    whatsapp: "Nomor WhatsApp",
    submit: "Jadwalkan demo 15 menit",
    submitting: "Mengirim…",
    orChat: "Atau langsung chat WhatsApp:",
    thankTitle: "Terima kasih! 🙏",
    thankBody:
      "Kami akan hubungi Anda via WhatsApp dalam 1×24 jam. Sampai jumpa di acara!",
    backHome: "Kembali ke awal",
    footer: "Dibuat di Indonesia 🇮🇩 · sorinsafety.com",
    successToast: "Kontak terkirim — sampai jumpa!",
    errorToast: "Gagal mengirim. Coba lagi atau chat WhatsApp.",
  },
  en: {
    eventBadge: "Indonesia Miners 2026",
    headline: "AI that prevents mining accidents — before they happen.",
    subhead:
      "Not a reporting app. The safety brain for your entire mine site.",
    pitchTitle: "What is SORIN? (the simple version)",
    pitchBody:
      "Imagine every worker has a personal safety mentor that chats via WhatsApp — knows who's fatigued today, where hazards cluster, and tells supervisors what to do — before anyone gets hurt.",
    howTitle: "How it works, in 3 steps",
    steps: [
      { n: "1", t: "LISTEN", d: "Workers chat via WhatsApp — voice or text. Done in 10 seconds." },
      { n: "2", t: "PREDICT", d: "AI reads patterns of fatigue, stress, near-miss, and risky behavior." },
      { n: "3", t: "ACT", d: "Supervisors get specific tasks — not long emails." },
    ],
    capabilityTitle: "What SORIN can do",
    capabilitySub: "One platform, full capability for modern HSE.",
    cards: [
      { icon: Mic, t: "10-second Hazard Reports", d: "Workers speak, AI understands, report is filed." },
      { icon: Brain, t: "Worker Risk Profiles", d: "Know who's high-risk before they get hurt." },
      { icon: Sparkles, t: "Incident Prediction", d: "AI reads weak signals and warns you early." },
      { icon: Bot, t: "Action Recommender", d: "Supervisors get AI-generated next actions." },
      { icon: MapPin, t: "Real-time Hazard Map", d: "See hotspots by location and by shift." },
      { icon: GraduationCap, t: "Auto Micro-learning", d: "60-second training pushed to workers who need it." },
    ],
    diffTitle: "What makes us different",
    diff: [
      "Native Bahasa Indonesia & WhatsApp — no app install needed.",
      "Built on behavioral science (UPPS-P psychometrics), not just a digital form.",
      "Designed for Indonesian mining: remote sites, weak signal, long shifts.",
    ],
    fitTitle: "SORIN fits if you…",
    fit: [
      "Have 50+ field workers",
      "Work with many contractors",
      "Struggle to get real leading indicators",
      "Are tired of HSE systems that are just form-fillers",
    ],
    ctaTitle: "Want to see SORIN running on your site?",
    ctaSub: "Drop your contact — we'll send a short 15-min demo. No hard sell.",
    name: "Name",
    company: "Company",
    role: "Role (optional)",
    whatsapp: "WhatsApp number",
    submit: "Book a 15-min demo",
    submitting: "Sending…",
    orChat: "Or chat WhatsApp directly:",
    thankTitle: "Thank you! 🙏",
    thankBody: "We'll reach out via WhatsApp within 24 hours. See you at the event!",
    backHome: "Back to top",
    footer: "Made in Indonesia 🇮🇩 · sorinsafety.com",
    successToast: "Contact sent — see you soon!",
    errorToast: "Failed to send. Try again or chat WhatsApp.",
  },
} as const;

const leadSchema = z.object({
  name: z.string().trim().min(2, "Min 2 characters").max(100),
  company: z.string().trim().min(2, "Min 2 characters").max(150),
  role: z.string().trim().max(100).optional().or(z.literal("")),
  whatsapp: z
    .string()
    .trim()
    .min(8, "Invalid number")
    .max(20)
    .regex(/^[+0-9\s()-]+$/, "Digits only"),
});

export default function MinersExplainer() {
  const [params] = useSearchParams();
  const [lang, setLang] = useState<Lang>(() =>
    (params.get("lang") as Lang) === "en" ? "en" : "id"
  );
  const t = copy[lang];

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", role: "", whatsapp: "" });

  const utm = useMemo(() => {
    const o: Record<string, string> = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "ctx"].forEach((k) => {
      const v = params.get(k);
      if (v) o[k] = v;
    });
    return o;
  }, [params]);

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    document.title = "SORIN — AI Safety untuk Tambang Indonesia";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta(
      "description",
      "SORIN: AI keselamatan kerja untuk tambang Indonesia. Prediksi insiden, profil risiko pekerja, action recommender — semua via WhatsApp."
    );
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://sorinsafety.com/m";
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = leadSchema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("event-lead-capture", {
        body: {
          ...parsed.data,
          source: "miners_2026",
          utm,
          user_agent: navigator.userAgent,
        },
      });
      if (error) throw error;
      setDone(true);
      toast.success(t.successToast);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      toast.error(t.errorToast);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur bg-background/80 border-b border-border/50">
        <div className="max-w-2xl mx-auto px-5 py-3 flex items-center justify-between">
          <img src={logoFull} alt="SORIN" className="h-7 w-auto" />
          <div className="flex items-center gap-1 text-xs font-medium">
            <button
              onClick={() => setLang("id")}
              className={`px-2 py-1 rounded ${lang === "id" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              ID
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-2 py-1 rounded ${lang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              EN
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pb-20">
        {/* HERO */}
        <section className="pt-10 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              {t.eventBadge}
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl font-bold leading-[1.1] tracking-tight">
              {t.headline}
            </h1>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
              {t.subhead}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <Button
                size="lg"
                className="rounded-full"
                onClick={() => document.getElementById("cta")?.scrollIntoView({ behavior: "smooth" })}
              >
                {t.submit}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </a>
            </div>
          </motion.div>
        </section>

        {/* PITCH */}
        <section className="py-10 border-t border-border/50">
          <h2 className="text-2xl font-bold mb-3">{t.pitchTitle}</h2>
          <p className="text-base leading-relaxed text-muted-foreground">
            {t.pitchBody}
          </p>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-10 border-t border-border/50">
          <h2 className="text-2xl font-bold mb-6">{t.howTitle}</h2>
          <div className="space-y-4">
            {t.steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex gap-4 p-4 rounded-2xl bg-muted/40"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center">
                  {s.n}
                </div>
                <div>
                  <div className="font-semibold tracking-wide text-sm">{s.t}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">{s.d}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CAPABILITIES */}
        <section className="py-10 border-t border-border/50">
          <h2 className="text-2xl font-bold">{t.capabilityTitle}</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">{t.capabilitySub}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {t.cards.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.t}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="p-4 rounded-2xl border border-border bg-card hover:shadow-md transition-shadow"
                >
                  <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-semibold text-sm">{c.t}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {c.d}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* DIFFERENTIATORS */}
        <section className="py-10 border-t border-border/50">
          <h2 className="text-2xl font-bold mb-5">{t.diffTitle}</h2>
          <ul className="space-y-3">
            {t.diff.map((d) => (
              <li key={d} className="flex gap-3 text-sm">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">{d}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* FIT */}
        <section className="py-10 border-t border-border/50">
          <h2 className="text-2xl font-bold mb-5">{t.fitTitle}</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {t.fit.map((f) => (
              <li
                key={f}
                className="flex gap-2 items-start text-sm p-3 rounded-xl bg-muted/40"
              >
                <span className="text-primary">✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section id="cta" className="py-10 border-t border-border/50">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-6 rounded-3xl bg-primary/5 border border-primary/20 text-center"
            >
              <div className="w-14 h-14 rounded-full bg-primary/10 text-primary mx-auto flex items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold">{t.thankTitle}</h3>
              <p className="text-sm text-muted-foreground mt-2">{t.thankBody}</p>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary"
              >
                <MessageCircle className="w-4 h-4" />
                +62 813-1083-3434
              </a>
              <div className="mt-4">
                <button
                  onClick={() => setDone(false)}
                  className="text-xs text-muted-foreground underline"
                >
                  {t.backHome}
                </button>
              </div>
            </motion.div>
          ) : (
            <div className="p-6 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20">
              <h3 className="text-2xl font-bold">{t.ctaTitle}</h3>
              <p className="text-sm text-muted-foreground mt-1.5">{t.ctaSub}</p>
              <form onSubmit={handleSubmit} className="mt-5 space-y-3">
                <div>
                  <Label htmlFor="name" className="text-xs">{t.name}</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    maxLength={100}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="text-xs">{t.company}</Label>
                  <Input
                    id="company"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    required
                    maxLength={150}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="role" className="text-xs">{t.role}</Label>
                  <Input
                    id="role"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    maxLength={100}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="whatsapp" className="text-xs">{t.whatsapp}</Label>
                  <Input
                    id="whatsapp"
                    type="tel"
                    inputMode="tel"
                    placeholder="+62 …"
                    value={form.whatsapp}
                    onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                    required
                    maxLength={20}
                    className="mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full rounded-full mt-2"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t.submitting}
                    </>
                  ) : (
                    <>
                      {t.submit}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
              <div className="mt-4 pt-4 border-t border-border/50 text-center">
                <div className="text-xs text-muted-foreground">{t.orChat}</div>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-1.5 text-sm font-semibold text-primary"
                >
                  <MessageCircle className="w-4 h-4" />
                  +62 813-1083-3434
                </a>
              </div>
            </div>
          )}
        </section>

        <footer className="pt-10 pb-4 text-center text-xs text-muted-foreground">
          {t.footer}
        </footer>
      </main>
    </div>
  );
}
